import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Breadcrumb } from './components/Breadcrumb';
import { AccessibilityMenu } from './components/AccessibilityMenu';
import { CookieBanner } from './components/CookieBanner';
import { MobileStickyBar } from './components/MobileStickyBar';

// Lazy load pages
const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const Menu = lazy(() => import('./pages/Menu').then(module => ({ default: module.Menu })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Events = lazy(() => import('./pages/Events').then(module => ({ default: module.Events })));
const OutdoorEvents = lazy(() => import('./pages/OutdoorEvents').then(module => ({ default: module.OutdoorEvents })));
const Gallery = lazy(() => import('./pages/Gallery').then(module => ({ default: module.Gallery })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Blog = lazy(() => import('./pages/Blog').then(module => ({ default: module.Blog })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(module => ({ default: module.BlogPost })));
const OutdoorEventsLanding = lazy(() => import('./pages/landing/OutdoorEventsLanding').then(module => ({ default: module.OutdoorEventsLanding })));

// Default exports
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const AccessibilityPage = lazy(() => import('./pages/Accessibility'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const FAQ = lazy(() => import('./pages/FAQ'));

const Loading = () => (
  <div className="flex items-center justify-center min-h-screen bg-black text-white">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
  </div>
);

// קומפוננטה לגלילה אוטומטית למעלה בכל מעבר עמוד
function ScrollToTop(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Campaign landing pages (/lp/...) stand alone, without the main site's navigation
function LandingApp(): JSX.Element {
  return (
    <HelmetProvider>
      <main className="relative min-h-screen">
        <ScrollToTop />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/lp/outdoor-events" element={<OutdoorEventsLanding />} />
          </Routes>
        </Suspense>
      </main>
      <AccessibilityMenu />
      <CookieBanner />
    </HelmetProvider>
  );
}

function App(): JSX.Element {
  const { pathname } = useLocation();
  if (pathname.startsWith('/lp/')) return <LandingApp />;

  return (
    <HelmetProvider>
      <div className="relative flex flex-col min-h-screen has-mobile-sticky">
        <Navbar />
        <Breadcrumb />
        <main className="flex-grow">
          <ScrollToTop />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/about" element={<About />} />
              <Route path="/events" element={<Events />} />
              <Route path="/outdoor-events" element={<OutdoorEvents />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/faq" element={<FAQ />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <MobileStickyBar />
        <AccessibilityMenu />
        <CookieBanner />
      </div>
    </HelmetProvider>
  );
}

export default App;